import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, Menu, Moon, OctagonAlert, Sun, TriangleAlert, X } from 'lucide-react'
import {
  Badge,
  Banner,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@regie/ui'
import { useAuthStore } from '@/stores/authStore'
import { useAlertSummary } from '@/features/alerts/api/hooks'
import { summaryTone } from '@/features/alerts/lib/alerts'
import { navItems } from './nav'

const THEME_KEY = 'regie.theme'
type Theme = 'light' | 'dark'

/**
 * Thumb-bar order: money screens first. Partenaires, Alertes and Audit stay in
 * the drawer — open alerts already surface through the global banner.
 */
const PRIMARY_NAV = ['/opportunities', '/conversions', '/properties', '/links'] as const

function readTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const toggle = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // ignore write failures (private mode, etc.)
    }
  }, [theme])
  return { theme, toggle }
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors duration-150 ease-out-expo',
    'min-h-11 lg:min-h-0 lg:py-1.5',
    isActive
      ? 'bg-primary/10 font-medium text-foreground'
      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
  )

export function AppShell() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    setNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!navOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navOpen])

  return (
    <div className="flex h-dvh bg-background text-foreground">
      <aside className="hidden w-[220px] shrink-0 flex-col border-r border-border bg-card lg:flex">
        <Brand />
        <SidebarNav />
        <div className="flex flex-col gap-0.5 border-t border-border p-2">
          <ThemeToggle />
          <UserMenu placement="sidebar" />
        </div>
      </aside>

      {navOpen ? (
        <div className="lg:hidden">
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="fixed inset-0 z-40 bg-[oklch(0.12_0.01_220/0.6)]"
            onClick={() => setNavOpen(false)}
          />
          <aside
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.menu')}
            className="fixed inset-y-0 left-0 z-50 flex w-[min(20rem,calc(100%-2.5rem))] flex-col border-r border-border bg-card pt-[env(safe-area-inset-top)]"
          >
            <Brand onClose={() => setNavOpen(false)} />
            <SidebarNav />
            <div className="flex flex-col gap-0.5 border-t border-border p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <ThemeToggle />
            </div>
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex min-h-12 items-center gap-2 border-b border-border bg-card px-3 pt-[env(safe-area-inset-top)] max-[380px]:min-h-11 [@media(orientation:landscape)_and_(max-height:500px)]:min-h-10 lg:hidden">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-[13px] font-semibold">
            R
          </span>
          <span className="text-sm font-semibold tracking-tight">{t('brand')}</span>
          <div className="ml-auto min-w-0">
            <UserMenu placement="topbar" />
          </div>
        </header>

        <AlertsBanner />

        <main className="min-w-0 flex-1 overflow-auto pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet />
        </main>

        <nav
          aria-label={t('nav.primary')}
          className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-card pb-[env(safe-area-inset-bottom)] lg:hidden"
        >
          {PRIMARY_NAV.map((to) => {
            const item = navItems.find((n) => n.to === to)
            if (!item) return null
            const { labelKey, icon: Icon } = item
            return (
              <NavLink
                key={to}
                to={to}
                aria-label={t(labelKey)}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[10px] leading-tight transition-colors duration-150 ease-out-expo',
                    'max-[380px]:min-h-11 [@media(orientation:landscape)_and_(max-height:500px)]:min-h-10 [@media(orientation:landscape)_and_(max-height:500px)]:gap-0',
                    isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate max-[380px]:sr-only [@media(orientation:landscape)_and_(max-height:500px)]:sr-only">
                  {t(labelKey)}
                </span>
              </NavLink>
            )
          })}
          <button
            type="button"
            aria-expanded={navOpen}
            aria-controls="mobile-nav"
            aria-label={t('nav.menu')}
            onClick={() => setNavOpen(true)}
            className="flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[10px] leading-tight text-muted-foreground transition-colors duration-150 ease-out-expo max-[380px]:min-h-11 [@media(orientation:landscape)_and_(max-height:500px)]:min-h-10 [@media(orientation:landscape)_and_(max-height:500px)]:gap-0"
          >
            <Menu className="h-4 w-4 shrink-0" />
            <span className="truncate max-[380px]:sr-only [@media(orientation:landscape)_and_(max-height:500px)]:sr-only">
              {t('nav.menu')}
            </span>
          </button>
        </nav>
      </div>
    </div>
  )
}

function Brand({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation('common')
  return (
    <div className="flex items-center gap-2 border-b border-border px-4 py-3.5">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-[13px] font-semibold">
        R
      </span>
      <span className="text-sm font-semibold tracking-tight">{t('brand')}</span>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label={t('actions.close')}
          className="ml-auto inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  )
}

function SidebarNav() {
  const { t } = useTranslation('common')
  return (
    <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
      {navItems.map(({ to, labelKey, icon: Icon }) => (
        <NavLink key={to} to={to} className={navLinkClass}>
          <Icon className="h-4 w-4 shrink-0" />
          {t(labelKey)}
          {to === '/alerts' ? <OpenAlertsCount /> : null}
        </NavLink>
      ))}
    </nav>
  )
}

/** Open-alert count next to the Alertes nav item; nothing when none or on error. */
function OpenAlertsCount() {
  const { t } = useTranslation('alerts')
  const { data: summary } = useAlertSummary()
  const tone = summaryTone(summary)
  if (!summary || !tone) return null
  return (
    <Badge tone={tone} className="ml-auto px-1.5 py-0 font-mono tabular-nums">
      <span aria-hidden>{summary.open}</span>
      <span className="sr-only">{t('badge.label', { count: summary.open })}</span>
    </Badge>
  )
}

/**
 * Global warning above every page while associated properties report open
 * alerts. Hidden on /alerts itself, and renders nothing if the summary request
 * fails — an alerting outage must never break the shell.
 */
function AlertsBanner() {
  const { t } = useTranslation('alerts')
  const { pathname } = useLocation()
  const { data: summary } = useAlertSummary()
  const tone = summaryTone(summary)
  if (!summary || !tone || pathname.startsWith('/alerts')) return null
  return (
    <div className="px-3 pt-3 min-[380px]:px-4 md:px-6 md:pt-4 lg:px-8">
      <Banner
        tone={tone}
        icon={tone === 'danger' ? OctagonAlert : TriangleAlert}
        title={t('banner.title', { count: summary.open })}
        description={
          summary.critical > 0 ? t('banner.critical', { count: summary.critical }) : undefined
        }
        action={
          <Button asChild variant="secondary" size="sm">
            <Link to="/alerts">{t('banner.action')}</Link>
          </Button>
        }
      />
    </div>
  )
}

function ThemeToggle() {
  const { t } = useTranslation('common')
  const { theme, toggle } = useTheme()
  const label = theme === 'dark' ? t('theme.light') : t('theme.dark')

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      className="flex min-h-11 items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card lg:min-h-0 lg:py-1.5"
    >
      {theme === 'dark' ? (
        <Sun className="h-4 w-4 shrink-0" />
      ) : (
        <Moon className="h-4 w-4 shrink-0" />
      )}
      {label}
    </button>
  )
}

function UserMenu({ placement }: { placement: 'sidebar' | 'topbar' }) {
  const { t } = useTranslation('common')
  const { t: tAuth } = useTranslation('auth')
  const navigate = useNavigate()
  const admin = useAuthStore((s) => s.admin)
  const logout = useAuthStore((s) => s.logout)

  const onLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-2.5 truncate rounded-md px-2.5 text-left text-[13px] text-muted-foreground transition-colors duration-150 ease-out-expo hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card data-[state=open]:bg-accent data-[state=open]:text-foreground',
            placement === 'sidebar'
              ? 'min-h-11 w-full lg:min-h-0 lg:py-1.5'
              : 'min-h-11 max-w-[11rem] max-[380px]:max-w-[7rem]',
          )}
        >
          <span className="truncate">{admin?.email ?? '—'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={placement === 'sidebar' ? 'top' : 'bottom'}
        align={placement === 'sidebar' ? 'start' : 'end'}
        className="w-52"
      >
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="truncate text-sm">{admin?.email}</span>
          {admin?.role && (
            <Badge tone="neutral" className="w-fit">
              {admin.role === 'super_admin' ? t('role.superAdmin') : t('role.tenantAdmin')}
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {tAuth('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
